import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';

// Styled wrapper for the toast
const ToastWrapper = styled(motion.div)(({ theme, type }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '16px',
  borderRadius: '10px',
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  backgroundColor: 'white',
  border: '1px solid',
  borderColor: (() => {
    switch (type) {
      case 'success':
        return '#ABEFC6';
      case 'error':
        return '#FFCDD2';
      case 'warning':
        return '#FFE0B2';
      case 'info':
        return '#B3E5FC';
      default:
        return '#E0E0E0';
    }
  })(),
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: '5px',
    background: (() => {
      switch (type) {
        case 'success':
          return '#4CAF50';
        case 'error':
          return '#F44336';
        case 'warning':
          return '#FF9800';
        case 'info':
          return '#2196F3';
        default:
          return '#757575';
      }
    })(),
  },
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

// Styled icon container
const IconBox = styled(Box)(({ theme, type }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '16px',
  color: (() => {
    switch (type) {
      case 'success':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      case 'warning':
        return '#FF9800';
      case 'info':
        return '#2196F3';
      default:
        return '#757575';
    }
  })(),
  '& svg': {
    fontSize: '24px',
  },
  [theme.breakpoints.down('sm')]: {
    marginRight: '12px',
    '& svg': {
      fontSize: '20px',
    },
  },
}));

// Content box
const ContentBox = styled(Box)({
  flexGrow: 1,
});

// Title styling
const ToastTitle = styled(Typography)(({ theme, type }) => ({
  fontWeight: 600,
  fontSize: '0.95rem',
  color: (() => {
    switch (type) {
      case 'success':
        return '#1B5E20';
      case 'error':
        return '#B71C1C';
      case 'warning':
        return '#E65100';
      case 'info':
        return '#0D47A1';
      default:
        return '#424242';
    }
  })(),
  marginBottom: '4px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
  },
}));

// Message styling
const ToastMessage = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: '#616161',
  lineHeight: 1.4,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const CustomToast = ({ message, type, closeToast }) => {
  // Get appropriate icon based on toast type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlinedIcon />;
      case 'error':
        return <ErrorOutlineOutlinedIcon />;
      case 'warning':
        return <WarningAmberOutlinedIcon />;
      case 'info':
        return <InfoOutlinedIcon />;
      default:
        return <InfoOutlinedIcon />;
    }
  };

  // Get appropriate title based on toast type
  const getTitle = () => {
    switch (type) {
      case 'success':
        return 'Success';
      case 'error':
        return 'Error';
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Information';
      default:
        return 'Notification';
    }
  };

  // Animation variants
  const variants = {
    initial: { 
      opacity: 0,
      x: 50,
      scale: 0.9,
    },
    animate: { 
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      }
    },
    exit: { 
      opacity: 0,
      x: 50,
      transition: {
        duration: 0.2,
      }
    }
  };

  return (
    <ToastWrapper
      type={type}
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
    >
      <IconBox type={type}>
        {getIcon()}
      </IconBox>
      <ContentBox>
        <ToastTitle type={type} variant="h6">
          {getTitle()}
        </ToastTitle>
        <ToastMessage variant="body2">
          {message}
        </ToastMessage>
      </ContentBox>
    </ToastWrapper>
  );
};

export default CustomToast; 